import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import {
  buildMonthGrid,
  dateFromIso,
  formatDisplayIso,
  isoFromDate,
  sameCalendarDay,
  weekdayNarrows,
} from './datePickerCalendarUtils.js';

const DEFAULT_LABELS = {
  openPicker: 'Calendar',
  prevMonth: 'Previous month',
  nextMonth: 'Next month',
  month: 'Month',
  year: 'Year',
  clear: 'Clear',
  today: 'Today',
  placeholder: 'dd/mm/yyyy',
};

const SELECT_CLASS =
  'rounded-md border border-edge bg-surface-muted px-2 py-1 text-[11px] text-content outline-none hover:border-accent/50 focus:border-accent/50';

/**
 * @typedef {'xs' | 'sm' | 'md' | 'xl'} DatePickerSize
 */

/** @type {Record<DatePickerSize, { trigger: string; calendarIcon: string }>} */
const TRIGGER_SIZE_PRESETS = {
  xs: {
    trigger:
      'min-h-8 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight transition-colors duration-150 flex items-center justify-between',
    calendarIcon: 'size-3 shrink-0',
  },
  sm: {
    trigger:
      'min-h-9 gap-2 rounded-md px-2 py-2 text-xs leading-snug transition-colors duration-150 flex items-center justify-between',
    calendarIcon: 'size-3.5 shrink-0',
  },
  md: {
    trigger:
      'min-h-10 gap-2 rounded-md px-2.5 py-2 text-sm leading-snug transition-colors duration-150 flex items-center justify-between',
    calendarIcon: 'size-4 shrink-0',
  },
  xl: {
    trigger:
      'min-h-[44px] gap-2 rounded-lg px-3 py-2.5 text-sm leading-normal transition-colors duration-150 flex items-center justify-between',
    calendarIcon: 'size-[18px] shrink-0',
  },
};

const TRIGGER_SHELL = [
  'w-full border border-edge bg-surface-muted text-left text-content',
  'placeholder:text-content-faint',
  /* Mismo acento que NumericField / campos formulario: purple accent, sin ring azul (brand). */
  'outline-none ring-0 ring-offset-0',
  'hover:border-accent/50',
  'focus:border-accent/50 focus:outline-none focus:ring-0 focus:ring-offset-0',
  'focus-visible:border-accent/50 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
  'aria-expanded:border-accent/50',
  'disabled:border-edge disabled:hover:border-edge disabled:focus:border-edge disabled:focus-visible:border-edge',
].join(' ');

const CalendarGrid = memo(function CalendarGrid({ grid, weekdays, selectedDate, today, onPick }) {
  return (
    <div className="grid grid-cols-7 gap-0.5 text-center">
      {weekdays.map((w, i) => (
        <div
          key={`w-${i}`}
          className="pb-1 text-[10px] font-semibold uppercase tracking-wide text-content-muted"
        >
          {w}
        </div>
      ))}
      {grid.map(({ date, inMonth }, idx) => {
        const isSel = selectedDate && sameCalendarDay(date, selectedDate);
        const isToday = sameCalendarDay(date, today);
        return (
          <button
            key={`d-${idx}`}
            type="button"
            onClick={() => onPick(date)}
            className={[
              'flex size-8 items-center justify-center rounded-md text-[11px] font-medium transition-colors',
              !inMonth ? 'text-content-muted/45 hover:bg-white/[0.04]' : 'text-content hover:bg-white/5',
              isSel ? '!bg-accent !text-white hover:!bg-accent' : '',
              isToday && !isSel ? 'border border-accent/50' : '',
            ].join(' ')}
          >
            {date.getDate()}
          </button>
        );
      })}
    </div>
  );
});

/**
 * Date field with calendar popover in a portal. Value is `YYYY-MM-DD` or `''` (same as native date input).
 * Densidad del **trigger** alineada con `NumericField`: `size` **xl** ⇒ `min-h-[44px]`.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {(next: string) => void} props.onChange
 * @param {DatePickerSize} [props.size='xs']
 * @param {string} [props.id]
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 * @param {string} [props.triggerClassName]
 * @param {string} [props.inputClassName]
 * @param {string} [props.placeholder]
 * @param {string} [props.locale]
 * @param {Partial<typeof DEFAULT_LABELS>} [props.labels]
 */
const DatePicker = ({
  value,
  onChange,
  id,
  disabled = false,
  className = '',
  triggerClassName = '',
  inputClassName = '',
  placeholder,
  locale = 'en-US',
  labels: labelsProp,
  size = 'xs',
}) => {
  const sz = TRIGGER_SIZE_PRESETS[size] ?? TRIGGER_SIZE_PRESETS.xs;
  const labels = useMemo(
    () => ({
      ...DEFAULT_LABELS,
      ...labelsProp,
      ...(placeholder !== undefined ? { placeholder } : {}),
    }),
    [labelsProp, placeholder],
  );

  const [open, setOpen] = useState(false);
  const [monthMenuOpen, setMonthMenuOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(() => ({ top: 0, left: 0, width: 0 }));
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const selectedDate = useMemo(() => dateFromIso(value), [value]);

  const today = useMemo(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  const initialView = selectedDate ?? today;
  const [viewYear, setViewYear] = useState(() => initialView.getFullYear());
  const [viewMonth0, setViewMonth0] = useState(() => initialView.getMonth());

  useEffect(() => {
    if (!open) return;
    const d = selectedDate ?? today;
    setViewYear(d.getFullYear());
    setViewMonth0(d.getMonth());
  }, [open, selectedDate, today]);

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
        new Date(viewYear, viewMonth0, 1),
      ),
    [locale, viewMonth0, viewYear],
  );

  const weekdays = useMemo(() => weekdayNarrows(locale), [locale]);
  const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth0), [viewMonth0, viewYear]);

  const displayValue = value ? formatDisplayIso(value, locale) : '';

  const closePopover = useCallback(() => {
    setOpen(false);
    setMonthMenuOpen(false);
  }, []);

  const pickDate = useCallback(
    (d) => {
      onChange(isoFromDate(d));
      closePopover();
    },
    [onChange, closePopover],
  );

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const popH = popoverRef.current ? popoverRef.current.getBoundingClientRect().height : 340;
    let top = rect.bottom + 8;
    if (top + popH > window.innerHeight - 12) {
      top = Math.max(12, rect.top - popH - 8);
    }
    const left = Math.min(rect.left, Math.max(12, window.innerWidth - 292));
    setPopoverStyle({ top, left, width: Math.max(rect.width, 280) });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = () => reposition();
    const onResize = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closePopover();
    };
    const onDown = (e) => {
      const t = e.target;
      if (t instanceof Node) {
        if (triggerRef.current?.contains(t)) return;
        if (popoverRef.current?.contains(t)) return;
      }
      closePopover();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, closePopover]);

  const goPrevMonth = () => {
    if (viewMonth0 === 0) {
      setViewMonth0(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth0((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth0 === 11) {
      setViewMonth0(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth0((m) => m + 1);
    }
  };

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, m) => ({
        value: m,
        label: new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2000, m, 1)),
      })),
    [locale],
  );

  const yearOptions = useMemo(() => {
    const y0 = today.getFullYear();
    const years = [];
    for (let y = y0 - 5; y <= y0 + 10; y += 1) years.push(y);
    return years;
  }, [today]);

  const mergedTriggerClass = [TRIGGER_SHELL, sz.trigger, triggerClassName, inputClassName]
    .filter(Boolean)
    .join(' ');

  const popover =
    open ? (
      <div
        ref={popoverRef}
        role="dialog"
        aria-label={labels.openPicker}
        className="fixed z-[120] rounded-xl border border-edge bg-surface p-3 shadow-2xl"
        style={{
          top: popoverStyle.top,
          left: popoverStyle.left,
          minWidth: popoverStyle.width,
        }}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <button
              type="button"
              className="flex min-w-0 items-center gap-0.5 text-left text-[12px] font-semibold text-content"
              onClick={() => setMonthMenuOpen((v) => !v)}
            >
              <span className="truncate capitalize">{monthLabel}</span>
              <ChevronDown className="size-3.5 shrink-0 text-content-muted" aria-hidden />
            </button>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              className="rounded-md p-1 text-content-muted transition-colors hover:bg-white/5 hover:text-content"
              aria-label={labels.prevMonth}
              onClick={goPrevMonth}
            >
              <ChevronUp className="size-4" aria-hidden />
            </button>
            <button
              type="button"
              className="rounded-md p-1 text-content-muted transition-colors hover:bg-white/5 hover:text-content"
              aria-label={labels.nextMonth}
              onClick={goNextMonth}
            >
              <ChevronDown className="size-4" aria-hidden />
            </button>
          </div>
        </div>

        {monthMenuOpen ? (
          <div className="mb-3 flex gap-2">
            <select
              className={`${SELECT_CLASS} min-w-0 flex-1`}
              value={viewMonth0}
              aria-label={labels.month}
              onChange={(e) => setViewMonth0(Number(e.target.value))}
            >
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              className={`${SELECT_CLASS} w-24`}
              value={viewYear}
              aria-label={labels.year}
              onChange={(e) => setViewYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <CalendarGrid
          grid={grid}
          weekdays={weekdays}
          selectedDate={selectedDate}
          today={today}
          onPick={pickDate}
        />

        <div className="mt-2 flex items-center justify-between border-t border-edge pt-2.5">
          <button
            type="button"
            className="text-[11px] font-medium text-accent hover:text-accent/80"
            onClick={() => {
              onChange('');
              closePopover();
            }}
          >
            {labels.clear}
          </button>
          <button
            type="button"
            className="text-[11px] font-medium text-accent hover:text-accent/80"
            onClick={() => {
              onChange(isoFromDate(today));
              closePopover();
            }}
          >
            {labels.today}
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className={className}>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => !disabled && setOpen((o) => !o)}
        className={[mergedTriggerClass, disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer']
          .filter(Boolean)
          .join(' ')}
      >
        <span className={value ? 'text-content-secondary' : 'text-content-faint'}>
          {displayValue || labels.placeholder}
        </span>
        <Calendar className={[sz.calendarIcon, 'text-content-muted'].join(' ')} strokeWidth={2} aria-hidden />
      </button>
      {typeof document !== 'undefined' && popover ? createPortal(popover, document.body) : null}
    </div>
  );
};

export default DatePicker;
